import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    TextField,
    Typography,
} from '@mui/material';
import Cookies from 'js-cookie';
import { jwtDecode } from 'jwt-decode';
import { useDarkMode } from '../App';
import { applyStoredPreferences } from '../utils/preferences';

const LoginPage = () => {
    const navigate = useNavigate();
    const { setDarkMode } = useDarkMode();

    const [identifier, setIdentifier] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleSubmit = async (event) => {
        event.preventDefault();
        setError(null);
        setLoading(true);

        try {
            const response = await fetch(`${process.env.REACT_APP_BACKEND_ADDRESS}/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ identifier, password }),
            });
            const data = await response.json();

            if (response.ok) {
                Cookies.set('token', data.token, { secure: true, expires: 7 });
                localStorage.setItem('user', JSON.stringify(jwtDecode(data.token).user_data));
                applyStoredPreferences();
                setDarkMode(localStorage.getItem('darkMode') === 'true');
                window.dispatchEvent(new Event('storage'));

                // Pre-fetch recipe list in the background
                fetch(`${process.env.REACT_APP_BACKEND_ADDRESS}/recipes`, {
                    headers: { 'Authorization': 'Bearer ' + data.token },
                })
                    .then(r => r.json())
                    .then(recipes => sessionStorage.setItem('recipeList', JSON.stringify(recipes)))
                    .catch(console.error);

                navigate('/');
            } else {
                setPassword('');
                setError(data.detail || 'Invalid credentials');
                setLoading(false);
            }
        } catch (err) {
            setPassword('');
            setError('Login failed. Please try again.');
            setLoading(false);
        }
    };

    return (
        <Box sx={{ minHeight: '100vh', background: '#f9f4f0', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 6, px: { xs: 2, sm: 0 } }}>
            <Box sx={{ width: '100%', maxWidth: 440 }}>
                {/* Brand header */}
                <Box sx={{ background: '#B61219', borderRadius: '12px 12px 0 0', px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                        component="img"
                        src="/mangiamangia-icon.jpg"
                        alt="Mangia! Mangia!"
                        sx={{ width: { xs: 48, sm: 64 }, height: { xs: 48, sm: 64 }, borderRadius: 1.5, flexShrink: 0, objectFit: 'cover' }}
                    />
                    <Box sx={{ textAlign: 'left', flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontFamily: 'Georgia, serif', fontSize: { xs: 16, sm: 20 }, fontWeight: 'bold', color: '#fff', letterSpacing: 1, lineHeight: 1.2 }}>
                            Sposito Family Cookbook
                        </Typography>
                        <Typography sx={{ fontFamily: 'Georgia, serif', fontSize: 11, color: 'rgba(255,255,255,0.75)', letterSpacing: 2, textTransform: 'uppercase', mt: 0.5 }}>
                            Mangia! Mangia!
                        </Typography>
                    </Box>
                    {/localhost|127\.0\.0\.1|-dev-/.test(process.env.REACT_APP_BACKEND_ADDRESS) && (
                        <Box sx={{ px: 1.5, py: 0.25, bgcolor: 'warning.main', borderRadius: 1, flexShrink: 0 }}>
                            <Typography variant="caption" sx={{ fontWeight: 700, color: 'warning.contrastText', letterSpacing: 1 }}>
                                DEV
                            </Typography>
                        </Box>
                    )}
                </Box>

                {/* Card body */}
                <Box
                    component="form"
                    onSubmit={handleSubmit}
                    sx={{ background: '#fff', borderRadius: '0 0 12px 12px', px: 4, py: 4, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', display: 'flex', flexDirection: 'column', gap: 2 }}
                >
                    <TextField
                        fullWidth label="Username or Email" type="text"
                        value={identifier} disabled={loading}
                        onChange={(e) => setIdentifier(e.target.value)}
                        autoComplete="username"
                        inputProps={{ sx: { '&:-webkit-autofill': { WebkitBoxShadow: '0 0 0 100px #fff inset', WebkitTextFillColor: 'inherit' } } }}
                    />
                    <TextField
                        fullWidth label="Password" type="password"
                        value={password} disabled={loading}
                        onChange={(e) => setPassword(e.target.value)}
                        autoComplete="current-password"
                        inputProps={{ sx: { '&:-webkit-autofill': { WebkitBoxShadow: '0 0 0 100px #fff inset', WebkitTextFillColor: 'inherit' } } }}
                    />

                    {error && <Alert severity="error">{error}</Alert>}

                    <Button
                        type="submit" fullWidth variant="contained" size="large"
                        disabled={loading || !identifier || !password}
                        sx={{ mt: 1, background: '#B61219', '&:hover': { background: '#5C0001' }, '&:disabled': { background: '#ccc' } }}
                    >
                        {loading ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
                    </Button>
                </Box>
            </Box>
        </Box>
    );
};

export default LoginPage;
